import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { PageHeader, Loading, Empty } from "../components/Layout";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, ShieldCheck, Download, Star, X } from "lucide-react";

const ACTION_LABEL = {
  lead_reassignment: "Lead reassignment",
  stage_extension: "Stage extension",
  bulk_import: "Bulk import",
  user_creation: "User creation",
  custom: "Custom request",
};

const STATUS_STYLE = {
  pending: { color: "#B8860B", label: "PENDING", Icon: Clock },
  approved: { color: "#3E8F99", label: "APPROVED", Icon: CheckCircle2 },
  rejected: { color: "#B23A2E", label: "REJECTED", Icon: XCircle },
};

export default function Approvals() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [actionType, setActionType] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [views, setViews] = useState([]);

  const loadViews = () => api.get("/approvals/views").then((r) => setViews(r.data));
  useEffect(() => { loadViews(); }, []);

  const load = (s = status, at = actionType, query = q) => {
    setRows(null);
    setSelected(new Set());
    const params = new URLSearchParams({ status: s });
    if (at) params.set("action_type", at);
    if (query) params.set("q", query);
    api.get(`/approvals?${params.toString()}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(status, actionType, q); /* eslint-disable-next-line */ }, [status, actionType]);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectable = (rows || []).filter((r) => r.status === "pending" && r.can_approve).map((r) => r.id);
  const allSelected = selectable.length > 0 && selectable.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable));

  const bulkDecide = async (action) => {
    const ids = [...selected];
    if (!ids.length) return;
    let reason = "";
    if (action === "reject") reason = window.prompt("Reason for rejecting these (optional):") || "";
    setBusy("bulk");
    try {
      const r = await api.post(`/approvals/bulk/${action}`, { ids, reason });
      toast.success(`${r.data.approved + r.data.rejected} processed · ${r.data.skipped} skipped${r.data.errors?.length ? ` · ${r.data.errors.length} errors` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams({ status });
      if (actionType) params.set("action_type", actionType);
      if (q) params.set("q", q);
      const r = await api.get(`/approvals/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "approvals_audit.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit trail exported");
    } catch (e) { toast.error("Export failed"); }
  };

  const saveView = async () => {
    const name = window.prompt("Name this view (e.g. Reassignments this week):");
    if (!name) return;
    try { await api.post("/approvals/views", { name, status, action_type: actionType, q }); toast.success("View saved"); loadViews(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const applyView = (v) => { setActionType(v.action_type || ""); setQ(v.q || ""); setStatus(v.status || "all"); load(v.status || "all", v.action_type || "", v.q || ""); };
  const deleteView = async (id) => { try { await api.delete(`/approvals/views/${id}`); loadViews(); } catch (e) { /* ignore */ } };

  const decide = async (id, action) => {
    let reason = "";
    if (action === "reject") {
      reason = window.prompt("Reason for rejecting (optional):") || "";
    }
    setBusy(id);
    try {
      const r = await api.post(`/approvals/${id}/${action}`, { reason });
      if (action === "approve") toast.success("Approved — action executed" + (r.data?.result?.generated_password ? ` · temp password: ${r.data.result.generated_password}` : ""));
      else toast.success("Request rejected");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <>
      <PageHeader eyebrow="GOVERNANCE · SIGN-OFF REQUIRED" title="Approvals" hint="Actions that need sign-off before they happen — stage moves, reassignments, discounts. Pending items you can decide on appear first; History is the permanent record." />
      <div className="p-8 space-y-5">
        <div className="flex gap-1 border-b border-[#DFDAD0] overflow-x-auto whitespace-nowrap" data-testid="approvals-tabs">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`approvals-tab-${s}`}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-wider border-b-2 -mb-px transition-colors ${status === s ? "border-[#B23A2E] text-[#1C2530]" : "border-transparent text-[#1C2530]/50 hover:text-[#1C2530]"}`}>
              {s === "all" ? "History" : s}
            </button>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-2" data-testid="approvals-filters">
          <select value={actionType} onChange={(e) => setActionType(e.target.value)} data-testid="approvals-filter-action"
            className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
            <option value="">All action types</option>
            {Object.keys(ACTION_LABEL).map((a) => <option key={a} value={a}>{ACTION_LABEL[a]}</option>)}
          </select>
          <div className="flex-1 flex flex-wrap gap-2 min-w-0">
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(status, actionType, q)}
              data-testid="approvals-search" placeholder="Search title, requester or approver…"
              className="flex-1 min-w-[140px] border border-[#DFDAD0] px-3 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
            <button onClick={() => load(status, actionType, q)} data-testid="approvals-search-btn"
              className="bg-[#1C2530] text-[#F5F3EE] px-4 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644]">Search</button>
            <button onClick={exportCsv} data-testid="approvals-export-btn"
              className="flex items-center gap-1 border border-[#DFDAD0] px-3 text-xs font-mono uppercase tracking-wider text-[#1C2530]/70 hover:bg-[#EFECE5]"><Download className="w-3.5 h-3.5" /> Export</button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="saved-views">
          <span className="font-mono text-[9px] uppercase text-[#1C2530]/40">saved views:</span>
          {views.map((v) => (
            <span key={v.id} className="flex items-center border border-[#DFDAD0]">
              <button onClick={() => applyView(v)} data-testid={`view-apply-${v.id}`} className="pl-2 py-1 text-xs text-[#1C2530]/70 hover:text-[#1C2530]">{v.name}</button>
              <button onClick={() => deleteView(v.id)} data-testid={`view-del-${v.id}`} className="px-1.5 py-1 text-[#B23A2E] hover:bg-[#B23A2E]/10"><X className="w-3 h-3" /></button>
            </span>
          ))}
          <button onClick={saveView} data-testid="view-save-btn" className="flex items-center gap-1 border border-dashed border-[#DFDAD0] px-2 py-1 text-xs text-[#1C2530]/60 hover:border-[#1C2530] hover:text-[#1C2530]"><Star className="w-3 h-3" /> Save current</button>
        </div>

        {status === "pending" && selectable.length > 0 && (
          <div className="flex items-center gap-3 bg-[#1C2530] text-[#F5F3EE] px-4 py-2" data-testid="bulk-bar">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="bulk-select-all" className="accent-white" />
              Select all ({selectable.length})
            </label>
            <span className="font-mono text-[10px] text-[#F5F3EE]/60">{selected.size} selected</span>
            <div className="ml-auto flex gap-2">
              <button onClick={() => bulkDecide("approve")} disabled={!selected.size || busy === "bulk"} data-testid="bulk-approve-btn"
                className="flex items-center gap-1 bg-[#3E8F99] text-white px-3 py-1 text-xs font-mono uppercase tracking-wider hover:opacity-90 disabled:opacity-40">
                <CheckCircle2 className="w-3.5 h-3.5" /> Approve selected
              </button>
              <button onClick={() => bulkDecide("reject")} disabled={!selected.size || busy === "bulk"} data-testid="bulk-reject-btn"
                className="flex items-center gap-1 bg-[#B23A2E] text-white px-3 py-1 text-xs font-mono uppercase tracking-wider hover:opacity-90 disabled:opacity-40">
                <XCircle className="w-3.5 h-3.5" /> Reject selected
              </button>
            </div>
          </div>
        )}

        {!rows ? <Loading /> : rows.length === 0 ? <Empty label={status === "all" ? "No history yet" : `No ${status} requests`} /> : (
          <div className="space-y-3" data-testid="approvals-list">
            {rows.map((r) => {
              const st = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
              return (
                <div key={r.id} className="border border-[#DFDAD0] bg-white p-4" data-testid={`approval-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                    {r.status === "pending" && r.can_approve && (
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} data-testid={`bulk-cb-${r.id}`}
                        className="mt-1 accent-[#1C2530]" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <ShieldCheck className="w-3.5 h-3.5 text-[#1C2530]/40" />
                        <span className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50 border border-[#DFDAD0] px-1">{ACTION_LABEL[r.action_type] || r.action_type}</span>
                        <span className="font-mono text-[9px] uppercase px-1.5 py-0.5 border" style={{ color: st.color, borderColor: st.color }}>{st.label}</span>
                      </div>
                      <div className="text-sm font-medium text-[#1C2530]">{r.title}</div>
                      {r.summary && <div className="text-xs text-[#1C2530]/60 mt-0.5">{r.summary}</div>}
                      {r.payload?.margin?.margin_amount != null && (
                        <div className="mt-2 inline-grid grid-cols-3 gap-px border border-[#DFDAD0] bg-[#DFDAD0] text-center" data-testid={`approval-margin-card-${r.id}`}>
                          {[["Net", r.payload.margin.net_amount], ["Partner cost", r.payload.margin.partner_cost], ["Gross margin", r.payload.margin.margin_amount]].map(([l, v], i) => (
                            <div key={l} className={`px-3 py-1.5 ${i === 2 ? (r.payload.margin_breach ? "bg-[#B23A2E]/10" : "bg-[#2F7A3E]/10") : "bg-white"}`}>
                              <div className="font-mono text-[8px] uppercase tracking-wider text-[#1C2530]/50">{l}</div>
                              <div className={`font-mono text-xs font-semibold ${i === 2 ? (r.payload.margin_breach ? "text-[#B23A2E]" : "text-[#2F7A3E]") : "text-[#1C2530]"}`}>
                                {Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}{i === 2 && r.payload.margin.margin_pct != null ? ` · ${r.payload.margin.margin_pct}%` : ""}
                              </div>
                            </div>
                          ))}
                          {r.payload.margin_breach && <div className="col-span-3 bg-[#B23A2E]/10 text-[#B23A2E] font-mono text-[9px] uppercase py-0.5" data-testid={`approval-margin-breach-${r.id}`}>Below margin floor</div>}
                        </div>
                      )}
                      <div className="font-mono text-[10px] text-[#1C2530]/40 mt-2">
                        Requested by {r.requested_by_name} ({r.requester_role})
                        {r.decided_by_name && ` · decided by ${r.decided_by_name}`}
                        {r.decision_reason && ` · "${r.decision_reason}"`}
                      </div>
                    </div>
                    {r.status === "pending" && r.can_approve && (
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => decide(r.id, "approve")} disabled={busy === r.id} data-testid={`approve-${r.id}`}
                          className="flex items-center gap-1 bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644] disabled:opacity-50">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button onClick={() => decide(r.id, "reject")} disabled={busy === r.id} data-testid={`reject-${r.id}`}
                          className="flex items-center gap-1 border border-[#B23A2E] text-[#B23A2E] px-3 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-[#B23A2E]/10 disabled:opacity-50">
                          <XCircle className="w-3.5 h-3.5" /> Reject
                        </button>
                      </div>
                    )}
                    {r.status === "pending" && !r.can_approve && r.mine && (
                      <span className="font-mono text-[10px] text-[#1C2530]/40 shrink-0">Awaiting approver</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
